// src/components/store/sections/shop/ShopOutletPage.tsx
"use client";

import { useState, useEffect, useMemo } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import {
  Heart,
  Search,
  Star,
  ChevronRight,
  ChevronLeft,
  LayoutGrid,
  List,
  Loader2,
  Store,
  Layers,
} from "lucide-react";
import { useCart } from "@/context/CartContext";

export interface ProductItem {
  id: number | string;
  name: string;
  slug?: string;
  price: number | string;
  compare_price?: number | string;
  regular_price?: number | string;
  primary_image?: string | null;
  image?: string | null;
  category_name?: string;
  category_id?: number | string;
  rating?: number;
}

export interface ShopCategoryItem {
  id: number | string;
  name: string;
  slug?: string;
  product_count?: number;
}

export interface ShopDetails {
  id: number | string;
  name: string;
  logo?: string | null;
  logo_url?: string | null;
  category_label?: string;
  description?: string;
}

interface ShopOutletPageProps {
  shopId: string;
  subdomain?: string;
}

export default function ShopOutletPage({ shopId, subdomain = "nextlook" }: ShopOutletPageProps) {
  const router = useRouter();
  const { addToCart, openCart } = useCart() as any;

  const [shop, setShop] = useState<ShopDetails | null>(null);
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [categories, setCategories] = useState<ShopCategoryItem[]>([]);
  const [loading, setLoading] = useState(true);

  // Filter States
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [maxPrice, setMaxPrice] = useState<number>(5000);
  const [selectedRating, setSelectedRating] = useState<number | null>(null);
  const [sortBy, setSortBy] = useState("default");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;

  // 🌟 লোকালহোস্ট এবং লাইভ/কাস্টম ডোমেইনে (অন্য পিসি বা ফোনে) ইমেজ যাতে না ভাঙে
  const formatImg = (img?: string | null) => {
    if (!img) {
      return "https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=600&auto=format&fit=crop&q=80";
    }
    if (img.startsWith("http://") || img.startsWith("https://")) {
      if (
        typeof window !== "undefined" &&
        !window.location.hostname.includes("localhost") &&
        !window.location.hostname.includes("127.0.0.1")
      ) {
        try {
          const parsed = new URL(img);
          if (parsed.hostname.includes("127.0.0.1") || parsed.hostname.includes("localhost")) {
            return `${window.location.origin}${parsed.pathname}${parsed.search}`;
          }
          if (window.location.protocol === "https:" && img.startsWith("http://")) {
            return img.replace(/^http:\/\//i, "https://");
          }
        } catch {
          return img;
        }
      }
      return img;
    }

    const cleanPath = img.startsWith("/") ? img : `/${img}`;
    if (
      typeof window !== "undefined" &&
      (window.location.hostname.includes("localhost") || window.location.hostname === "127.0.0.1")
    ) {
      return `http://127.0.0.1:8000${cleanPath}`;
    }
    return cleanPath;
  };

  // 🌟 শুধু ওই শপের ইনফো, ক্যাটাগরি এবং প্রোডাক্ট ফেচ করা
  useEffect(() => {
    async function fetchShopData() {
      setLoading(true);
      try {
        const isLocal =
          typeof window !== "undefined" &&
          (window.location.hostname.includes("localhost") ||
            window.location.hostname === "127.0.0.1");

        const apiBase = isLocal ? `http://${subdomain}.localhost:8000` : "";

        // ১. Shop Details Fetch
        const shopRes = await fetch(`${apiBase}/api/v1/stores/shops/${shopId}/?subdomain=${subdomain}`);
        if (shopRes.ok) {
          const sData = await shopRes.json();
          setShop(sData);
        } else {
          const listRes = await fetch(`${apiBase}/api/v1/stores/shops/?subdomain=${subdomain}`);
          if (listRes.ok) {
            const listData = await listRes.json();
            const list = Array.isArray(listData) ? listData : listData?.results || [];
            const matched = list.find((s: any) => String(s.id) === String(shopId));
            if (matched) setShop(matched);
          }
        }

        // ২. শুধু এই Shop-এর আন্ডারে থাকা Category গুলো ফেচ করা
        const catRes = await fetch(`${apiBase}/api/v1/products/categories/?shop=${shopId}&subdomain=${subdomain}`);
        if (catRes.ok) {
          const cData = await catRes.json();
          setCategories(Array.isArray(cData) ? cData : cData?.results || []);
        }

        // ৩. শুধু এই Shop-এর Product গুলো ফেচ করা
        const prodRes = await fetch(`${apiBase}/api/v1/products/?shop=${shopId}&subdomain=${subdomain}`);
        if (prodRes.ok) {
          const pData = await prodRes.json();
          const pList = Array.isArray(pData) ? pData : pData?.results || [];
          setProducts(pList);
        }
      } catch (err) {
        console.error("Error loading shop catalog:", err);
      } finally {
        setLoading(false);
      }
    }

    if (shopId) {
      fetchShopData();
    }
  }, [shopId, subdomain]);

  const toggleCategory = (catIdentifier: string) => {
    setSelectedCategories((prev) =>
      prev.includes(catIdentifier)
        ? prev.filter((c) => c !== catIdentifier)
        : [...prev, catIdentifier]
    );
    setCurrentPage(1);
  };

  // 🌟 Filter Calculation
  const filteredProducts = useMemo(() => {
    let result = [...products];

    if (selectedCategories.length > 0) {
      result = result.filter((p) =>
        selectedCategories.some(
          (cat) =>
            String(p.category_id) === cat ||
            p.category_name?.toLowerCase() === cat.toLowerCase()
        )
      );
    }

    result = result.filter((p) => Number(p.price) <= maxPrice);

    if (selectedRating !== null) {
      result = result.filter((p) => Math.round(Number(p.rating) || 5) >= selectedRating);
    }

    if (searchQuery.trim()) {
      result = result.filter((p) =>
        p.name.toLowerCase().includes(searchQuery.toLowerCase().trim())
      );
    }

    if (sortBy === "price_low") {
      result.sort((a, b) => Number(a.price) - Number(b.price));
    } else if (sortBy === "price_high") {
      result.sort((a, b) => Number(b.price) - Number(a.price));
    }

    return result;
  }, [products, selectedCategories, maxPrice, selectedRating, searchQuery, sortBy]);

  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage) || 1;
  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredProducts.slice(start, start + itemsPerPage);
  }, [filteredProducts, currentPage]);

  const latestProductsWidget = useMemo(() => products.slice(0, 3), [products]);

  const handleAddToCart = (e: React.MouseEvent, product: ProductItem) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product, 1);
    if (openCart) openCart();
  };

  const handleOrderNow = (e: React.MouseEvent, product: ProductItem) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product, 1);
    router.push("/checkout");
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] bg-white flex flex-col items-center justify-center gap-3 font-sans">
        <Loader2 className="w-10 h-10 animate-spin text-purple-700" />
        <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">
          Loading {shop?.name || "Shop"} Catalog...
        </span>
      </div>
    );
  }

  const shopName = shop?.name || "Official Shop";

  return (
    <div className="w-full bg-white text-slate-800 font-sans select-none">
      {/* 🌟 শুধুমাত্র শপের ক্লিন টাইটেল ব্যানার (আলাদা কোনো ডুপ্লিকেট Header বা Footer নেই) */}
      <section className="w-full bg-[#fce7f3]/40 border-b border-pink-100 py-8 px-4 relative overflow-hidden flex flex-col items-center justify-center text-center">
        <span className="text-[10px] font-black uppercase tracking-widest text-purple-600 mb-1">
          {shop?.category_label || "Official Brand Outlet"}
        </span>
        <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight relative z-10 uppercase">
          {shopName}
        </h1>
        <div className="flex items-center gap-1.5 text-xs text-slate-500 font-bold mt-1.5 relative z-10">
          <Link href="/" className="hover:text-purple-700">
            Home
          </Link>
          <span>&gt;</span>
          <Link href="/products" className="hover:text-purple-700">
            All Products
          </Link>
          <span>&gt;</span>
          <span className="text-purple-700">{shopName}</span>
        </div>
      </section>

      {/* 🌟 মূল ক্যাটালগ কনটেন্ট */}
      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* ===================== বাম পাশের ফিল্টার সাইডবার ===================== */}
          <aside className="lg:col-span-3 space-y-7 text-left">
            {/* ১. শপের ভেতর প্রোডাক্ট সার্চ */}
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder={`Search in ${shopName}...`}
                className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-200 text-xs text-slate-800 outline-none focus:border-purple-600"
              />
            </div>

            {/* ২. Shop-এর ক্যাটাগরি চেকলিস্ট */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-black text-slate-900 tracking-tight flex items-center gap-1.5">
                  <Layers className="w-4 h-4 text-purple-600" />
                  <span>Category</span>
                </h3>
                {selectedCategories.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setSelectedCategories([])}
                    className="text-[10px] font-bold text-purple-600 hover:underline cursor-pointer"
                  >
                    Clear
                  </button>
                )}
              </div>

              {categories.length === 0 ? (
                <p className="text-xs text-slate-400 italic">No specific categories under this shop.</p>
              ) : (
                <div className="space-y-2 text-xs font-semibold text-slate-600">
                  {categories.map((cat) => {
                    const isChecked =
                      selectedCategories.includes(cat.name) ||
                      selectedCategories.includes(String(cat.id));
                    return (
                      <label
                        key={cat.id}
                        className="flex items-center justify-between cursor-pointer hover:text-slate-900 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleCategory(cat.name)}
                            className="rounded border-slate-300 text-purple-600 focus:ring-purple-500 w-3.5 h-3.5 cursor-pointer"
                          />
                          <span>{cat.name}</span>
                        </div>
                        {cat.product_count !== undefined && (
                          <span className="text-[10px] text-slate-400 font-mono font-bold">
                            ({cat.product_count})
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ৩. প্রাইস রেঞ্জ ফিল্টার */}
            <div className="space-y-3">
              <h3 className="text-base font-black text-slate-900 tracking-tight">Price</h3>
              <div className="space-y-2">
                <input
                  type="range"
                  min="50"
                  max="10000"
                  step="50"
                  value={maxPrice}
                  onChange={(e) => {
                    setMaxPrice(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="w-full accent-[#00a86b] cursor-pointer"
                />
                <div className="text-xs font-bold text-slate-700 font-mono">
                  ৳50 - ৳{maxPrice}
                </div>
              </div>
            </div>

            {/* ৪. স্টার রেটিং ফিল্টার */}
            <div className="space-y-3">
              <h3 className="text-base font-black text-slate-900 tracking-tight">Rating</h3>
              <div className="space-y-1.5">
                {[5, 4, 3, 2, 1].map((stars) => (
                  <div
                    key={stars}
                    onClick={() => {
                      setSelectedRating(selectedRating === stars ? null : stars);
                      setCurrentPage(1);
                    }}
                    className={`flex items-center gap-1 cursor-pointer p-1 rounded transition-colors ${
                      selectedRating === stars ? "bg-purple-50" : "hover:bg-slate-50"
                    }`}
                  >
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star
                        key={s}
                        className={`w-3.5 h-3.5 ${
                          s <= stars
                            ? "fill-amber-400 text-amber-400"
                            : "fill-slate-200 text-slate-200"
                        }`}
                      />
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {/* ৫. Latest Product উইজেট */}
            <div className="space-y-3 pt-2">
              <div className="flex items-center justify-between border-b border-slate-200 pb-1.5">
                <h3 className="text-xs font-black uppercase text-slate-900">Latest Product</h3>
                <div className="flex gap-1 text-slate-400">
                  <ChevronLeft className="w-3.5 h-3.5 hover:text-slate-800 cursor-pointer" />
                  <ChevronRight className="w-3.5 h-3.5 hover:text-slate-800 cursor-pointer" />
                </div>
              </div>

              <div className="space-y-3">
                {latestProductsWidget.map((item) => (
                  <Link
                    key={item.id}
                    href={`/products/${item.slug || item.id}`}
                    className="flex items-center gap-3 group"
                  >
                    <div className="w-12 h-12 rounded bg-slate-50 overflow-hidden shrink-0 border border-slate-200">
                      <img
                        src={formatImg(item.primary_image || item.image)}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="min-w-0">
                      <h4
                        className="text-xs font-bold text-slate-800 group-hover:text-purple-700 truncate"
                        title={item.name}
                      >
                        {item.name}
                      </h4>
                      <span className="text-xs font-black text-purple-700 font-mono">
                        ৳{Math.round(Number(item.price))}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </aside>

          {/* ===================== ডান পাশের প্রোডাক্ট গ্রিড ===================== */}
          <section className="lg:col-span-9 space-y-6">
            {/* টুলবার */}
            <div className="flex items-center justify-between pb-3 border-b border-slate-200 text-xs text-slate-500">
              <span className="font-bold text-slate-700">
                ({filteredProducts.length}) Products Found
              </span>

              <div className="flex items-center gap-3">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="bg-white border border-slate-200 rounded px-3 py-1.5 text-xs outline-none cursor-pointer"
                >
                  <option value="default">Sort By: Default</option>
                  <option value="price_low">Price: Low to High</option>
                  <option value="price_high">Price: High to Low</option>
                </select>

                <div className="flex items-center border border-slate-200 rounded overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setViewMode("grid")}
                    className={`p-1.5 cursor-pointer ${
                      viewMode === "grid"
                        ? "bg-slate-200 text-slate-900"
                        : "bg-white text-slate-500"
                    }`}
                  >
                    <LayoutGrid className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode("list")}
                    className={`p-1.5 cursor-pointer ${
                      viewMode === "list"
                        ? "bg-slate-200 text-slate-900"
                        : "bg-white text-slate-500"
                    }`}
                  >
                    <List className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>

            {/* প্রোডাক্ট কার্ডস */}
            {filteredProducts.length === 0 ? (
              <div className="py-24 text-center text-slate-400 bg-white rounded-2xl border border-slate-200">
                <Store className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                <p className="text-sm font-bold">এই শপে কোনো ম্যাচিং প্রোডাক্ট পাওয়া যায়নি।</p>
              </div>
            ) : (
              <div
                className={`grid ${
                  viewMode === "grid"
                    ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
                    : "grid-cols-1 gap-4"
                }`}
              >
                {paginatedProducts.map((p) => {
                  const price = Number(p.price) || 0;
                  const imgUrl = formatImg(p.primary_image || p.image);

                  return (
                    <div
                      key={p.id}
                      className="bg-white rounded-lg border border-slate-200 hover:shadow-lg transition-all duration-300 flex flex-col justify-between overflow-hidden group"
                    >
                      <Link
                        href={`/products/${p.slug || p.id}`}
                        className="relative w-full aspect-square bg-slate-50 overflow-hidden flex items-center justify-center p-2"
                      >
                        <img
                          src={imgUrl}
                          alt={p.name}
                          className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                        />
                      </Link>

                      <div className="p-3.5 space-y-2.5 flex flex-col justify-between flex-1 text-left">
                        <div>
                          <Link href={`/products/${p.slug || p.id}`}>
                            <h4
                              className="text-xs font-black text-slate-900 line-clamp-1 hover:text-purple-700 transition-colors"
                              title={p.name}
                            >
                              {p.name}
                            </h4>
                          </Link>

                          <div className="flex items-center justify-between mt-1.5">
                            <span className="text-xs font-black text-slate-900 font-mono">
                              ৳{Math.round(price).toLocaleString()}
                            </span>
                            <div className="flex text-amber-400">
                              {[1, 2, 3, 4, 5].map((s) => (
                                <Star key={s} className="w-2.5 h-2.5 fill-current" />
                              ))}
                            </div>
                          </div>
                        </div>

                        <div className="space-y-1.5 pt-1">
                          <div className="flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={(e) => handleAddToCart(e, p)}
                              className="flex-1 py-1.5 rounded border border-[#6b21a8] text-[#6b21a8] hover:bg-[#6b21a8] hover:text-white font-black text-[10px] uppercase transition-colors cursor-pointer text-center"
                            >
                              Add To Cart
                            </button>
                            <button
                              type="button"
                              className="w-8 h-8 rounded bg-[#00a86b] hover:bg-[#059669] text-white flex items-center justify-center shrink-0 cursor-pointer shadow-xs transition-colors"
                              title="Wishlist"
                            >
                              <Heart className="w-3.5 h-3.5 fill-current" />
                            </button>
                          </div>

                          <button
                            type="button"
                            onClick={(e) => handleOrderNow(e, p)}
                            className="w-full py-1.5 rounded bg-[#6b21a8] hover:bg-[#581c87] text-white font-black text-[10px] uppercase tracking-wider transition-colors cursor-pointer shadow-xs text-center"
                          >
                            Order Now
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* সার্কুলার পেজিনেশন */}
            {totalPages > 1 && (
              <div className="pt-6 flex items-center justify-center gap-2">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((pg) => (
                  <button
                    key={pg}
                    type="button"
                    onClick={() => setCurrentPage(pg)}
                    className={`w-7 h-7 rounded-full text-xs font-bold transition-all cursor-pointer flex items-center justify-center ${
                      pg === currentPage
                        ? "bg-[#00a86b] text-white shadow-xs"
                        : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-100"
                    }`}
                  >
                    {pg}
                  </button>
                ))}

                {currentPage < totalPages && (
                  <button
                    type="button"
                    onClick={() => setCurrentPage((prev) => prev + 1)}
                    className="w-7 h-7 rounded-full border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 text-xs font-bold flex items-center justify-center cursor-pointer"
                  >
                    &raquo;
                  </button>
                )}
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}